import { apiSlice } from "../../app/apiSlice";
import { withDefaultMeta } from "../../util/withDefaultMeta";

export const videoApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => {
    const query = withDefaultMeta(builder.query);
    const mutation = withDefaultMeta(builder.mutation);

    return {
      getFilteredVideos: builder.query({
        query: ({ videoType, searchTerm, category, page, limit }) => {
          // Construct the URL with pagination parameters
          const params = new URLSearchParams();
          if (videoType) params.append("videoType", videoType);
          if (searchTerm) params.append("searchTerm", searchTerm);
          if (category) params.append("category", category);
          params.append("page", page);
          params.append("limit", limit);

          return `/videos?${params.toString()}`;
        },
        meta: { requiresAuth: false },
      }),

      // 🔹 Admin fetch all videos (requires auth)
      getAdminVideos: query({
        query: () => `/videos/admin`,
        providesTags: ["Video"],
      }),

      getFeaturedVideo: query({
        query: () => "/videos/featured",
        providesTags: ["Video"],
        meta: { requiresAuth: false },
      }),

      setFeaturedVideo: builder.mutation({
        query: ({ videoId, featuredUntil }) => ({
          url: "/videos/featured",
          method: "POST",
          body: { videoId, featuredUntil },
        }),
        invalidatesTags: ["Video"],
      }),

      // 🔹 Create video entry (Admin only)
      createVideoEntry: mutation({
        query: (videoData) => ({
          url: "/videos",
          method: "POST",
          body: videoData,
        }),
        invalidatesTags: ["Video"],
      }),

      // 🔹 Update video (Admin only)
      updateVideo: builder.mutation({
        query: ({ id, formData }) => ({
          url: `/videos/${id}/update-video`,
          method: "PUT",
          body: formData,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: "Video", id: arg.id },
        ],
      }),

      // 🔹 Add product to video (Admin only)
      addProductToVideo: builder.mutation({
        query: ({ videoId, productId }) => ({
          url: `/videos/${videoId}/add-product`,
          method: "PUT",
          body: { productId },
        }),
      }),

      // 🔹 Remove product from video (Admin only)
      removeProductFromVideo: builder.mutation({
        query: ({ videoId, productId }) => ({
          url: `/videos/${videoId}/remove-product/${productId}`,
          method: "DELETE",
        }),
      }),

      getVideoById: builder.query({
        query: (idOrSlug) => `/videos/${idOrSlug}`, // ✅ works with both ID and slug
        providesTags: (result, error, idOrSlug) => [
          { type: "Video", id: idOrSlug },
        ],
        meta: { requiresAuth: false },
      }),

      deleteVideo: builder.mutation({
        query: ({ videoId }) => ({
          url: `/videos/${videoId}`,
          method: "DELETE",
        }),
        invalidatesTags: (result, error, arg) => [
          { type: "Video", id: arg.id },
        ],
      }),

      incrementShareCount: builder.mutation({
        query: (videoId) => ({
          url: `/videos/share/${videoId}`,
          method: "PUT",
        }),
        // Invalidate the cache for the specific video to trigger a re-fetch
        invalidatesTags: (result, error, videoId) => [
          { type: "Video", id: videoId },
        ],
      }),

      addView: builder.mutation({
        query: ({ videoId, token, sessionId }) => ({
          url: "/videos/views",
          method: "PUT",
          body: { videoId },
          headers: token ? {} : { "x-session-id": sessionId },
        }),
        invalidatesTags: (result, error, { videoId }) => [
          { type: "Video", id: videoId },
        ],
        meta: { requiresAuth: false },
        async onQueryStarted({ videoId }, { dispatch, queryFulfilled }) {
          // Optimistically update getVideoById cache
          const patchResult = dispatch(
            videoApiSlice.util.updateQueryData(
              "getVideoById",
              videoId,
              (draft) => {
                if (draft) {
                  // Update numViews array
                  if (!draft.numViews) draft.numViews = [];
                  draft.numViews.push({
                    viewerId: "optimistic",
                    viewedAt: new Date().toISOString(),
                  });

                  // Update totalViews
                  draft.totalViews = (draft.totalViews || 0) + 1;

                  // Update todayViews
                  const startOfDay = new Date();
                  startOfDay.setHours(0, 0, 0, 0);
                  const viewedToday =
                    new Date().toISOString() >= startOfDay.toISOString();
                  draft.todayViews =
                    (draft.todayViews || 0) + (viewedToday ? 1 : 0);
                }
              }
            )
          );

          try {
            const { data } = await queryFulfilled;
            // Replace optimistic counts with real counts from server
            dispatch(
              videoApiSlice.util.updateQueryData(
                "getVideoById",
                videoId,
                (draft) => {
                  draft.numViews = data.numViews;
                  draft.totalViews = data.totalViews;
                  draft.todayViews = data.todayViews;
                }
              )
            );
          } catch {
            // Rollback if request fails
            patchResult.undo();
          }
        },
      }),
    };
  },
});

export const {
  useGetFeaturedVideoQuery,
  useSetFeaturedVideoMutation,
  useGetVideoByIdQuery,
  useCreateVideoEntryMutation,
  useGetFilteredVideosQuery,
  useGetAdminVideosQuery,
  useAddProductToVideoMutation,
  useRemoveProductFromVideoMutation,
  useUpdateVideoMutation,
  useDeleteVideoMutation,
  useAddViewMutation,
  useIncrementShareCountMutation,
} = videoApiSlice;
